import React, { useState } from 'react';
import type { Product, StockMovement } from '../../types/product';
import { Edit, TrendingUp, TrendingDown } from 'lucide-react';

interface ProductDetailsProps {
  product: Product;
  stockMovements: StockMovement[];
  onEdit: () => void;
  onStockMovement: (type: 'in' | 'out', quantity: number) => void;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  stockMovements,
  onEdit,
  onStockMovement
}) => {
  const [quantity, setQuantity] = useState('');
  const [movementType, setMovementType] = useState<'in' | 'out'>('in');
  const [reason, setReason] = useState('');

  const handleStockMovement = () => {
    const parsedQuantity = parseInt(quantity);
    if (!isNaN(parsedQuantity) && parsedQuantity > 0) {
      onStockMovement(movementType, parsedQuantity);
      setQuantity('');
      setReason('');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{product.name}</h2>
          <button
            onClick={onEdit}
            className="p-2 text-gray-600 hover:text-gray-900"
          >
            <Edit className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-500">Código Interno</p>
            <p className="text-lg font-medium">{product.internalCode}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Código de Barras</p>
            <p className="text-lg font-medium">{product.barcode}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Categoria</p>
            <p className="text-lg font-medium">{product.category}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Subcategoria</p>
            <p className="text-lg font-medium">{product.subcategory}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Preço de Custo</p>
            <p className="text-lg font-medium">R$ {product.costPrice.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Preço de Venda</p>
            <p className="text-lg font-medium">R$ {product.sellingPrice.toFixed(2)}</p>
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Movimentação de Estoque</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Movimento
              </label>
              <select
                value={movementType}
                onChange={(e) => setMovementType(e.target.value as 'in' | 'out')}
                className="w-full border rounded-md p-2"
              >
                <option value="in">Entrada</option>
                <option value="out">Saída</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantidade
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full border rounded-md p-2"
                min="1"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border rounded-md p-2"
              placeholder="Informe o motivo da movimentação"
            />
          </div>

          <button
            onClick={handleStockMovement}
            disabled={!quantity || !reason}
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300"
          >
            Registrar Movimentação
          </button>
        </div>

        <div className="border-t mt-6 pt-6">
          <h3 className="text-lg font-semibold mb-4">Histórico de Movimentações</h3>
          <div className="space-y-4">
            {stockMovements.map((movement) => (
              <div key={movement.id} className="flex items-center gap-4">
                {movement.type === 'in' ? (
                  <TrendingUp className="h-5 w-5 text-green-500" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium">
                    {movement.type === 'in' ? 'Entrada' : 'Saída'} de {movement.quantity} unidades
                  </p>
                  <p className="text-sm text-gray-500">{movement.reason}</p>
                  <p className="text-xs text-gray-400">
                    {movement.date.toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;